import React, { useState } from 'react';
import { Card } from '../../../../../../components/Card/Card';
import { Event } from '../../../../../../model/event';
import { t } from '../../../../../../locales/translate';
import { LabelledProgress } from '../../../../../../components/LabelledProgress/LabelledProgress';
import { formatPercentage, formatDateTime } from '../../../../../../util/util';
import { KeyValueTable } from '../../../../../../components/KeyValueTable/KeyValueTable';
import { Stack } from '../../../../../../components/Stack/Stack';
import { Tooltip } from '../../../../../../components/Tooltip/Tooltip';
import { Icon } from '../../../../../../components/Icon/Icon';
import { Modal } from '../../../../../../components/Modal/Modal';
import { SalesReport } from '../SalesReport';
import { Button } from '../../../../../../components/Button/Button';

interface Props {
  event: Event;
}

export function TicketDist({ event }: Props): JSX.Element {
  const [salesReportOpen, setSalesReportOpen] = useState(false);
  const showTicketDist = event.salesStats?.soldHard || event.salesStats?.soldOnline;

  return (
    <section className="event-overview__section">
      <Card
        title={t.model.ticket.soldTicketsDist}
        className="labelled-progress"
        actions={
          <Button
            size="small"
            title={t.page.salesReport.downloadButton}
            onClick={() => setSalesReportOpen(true)}
            icon="download--sm"
          />
        }
      >
        <Stack size="lg">
          {showTicketDist ? (
            <LabelledProgress
              values={[
                {
                  value: event.salesStats?.soldHard || 0,
                  label: t.model.ticket.hardticket,
                },
                {
                  value: event.salesStats?.soldOnline || 0,
                  label: t.model.ticket.onlineticket,
                },
              ]}
            />
          ) : (
            <span>{t.model.ticket.noTicketsSold}</span>
          )}
          {(event.personalizationRequirement === 'contact_tracing_required' ||
            event.personalizationRequirement === 'check_in_required') &&
            event.salesStats && (
              <LabelledProgress
                values={[
                  {
                    value: event.salesStats.personalized,
                    label:
                      event.personalizationRequirement === 'check_in_required'
                        ? t.model.ticket.checkedIn
                        : t.model.ticket.personalized,
                    displayValue: `${event.salesStats.personalized} (${formatPercentage(
                      (event.salesStats.personalized * 1.0) / event.salesStats.sold,
                      { maximumFractionDigits: 0 }
                    )})`,
                  },
                  {
                    value: event.salesStats.sold - event.salesStats.personalized,
                    label:
                      event.personalizationRequirement === 'check_in_required'
                        ? t.model.ticket.notCheckedIn
                        : t.model.ticket.notPersonalized,
                    color: 'lightgrey',
                  },
                ]}
              />
            )}
          {event.personalizationRequirement === 'check_in_required' && (
            <KeyValueTable
              items={[
                { key: t.model.ticket.checkInOpens, value: formatDateTime(event.checkInStartAt) },
                {
                  key: t.model.ticket.checkInCloses,
                  keyNode: (
                    <span>
                      {t.model.ticket.checkInCloses}{' '}
                      <Tooltip style={{ maxWidth: '280px' }} reference={<Icon name="question--sm" />}>
                        {t.model.ticket.checkInClosesExplanation}
                      </Tooltip>
                    </span>
                  ),
                  value: formatDateTime(event.checkInEndAt),
                },
              ]}
            />
          )}
        </Stack>
      </Card>
      <Modal open={salesReportOpen} title={t.page.salesReport.title} size="small">
        {() => <SalesReport event={event} onClose={() => setSalesReportOpen(false)} />}
      </Modal>
    </section>
  );
}
